// Scores: what a position is worth, and how it is written.

import type { Score } from "./lines.ts";

export type { Score };

/** Win-chances in [-1, 1]: the scale every threshold in the analysis is
 *  expressed on. */
export function winChances(score: Score | null | undefined): number {
  if (!score || typeof score.value !== "number") return 0;
  if (score.kind === "mate") return score.value > 0 ? 1 : -1;
  return 2 / (1 + Math.exp(-0.00368208 * score.value)) - 1;
}

/**
 * A score from the solver's side, turned round to White's.
 *
 * Every number that reaches the board is scored from the solver's side,
 * because the sentences beside them are in the second person. The numbers
 * themselves are read as chess evaluations, and those are White's on every
 * board ever drawn, so they are turned at the point of display. Which side the
 * solver is on is in the puzzle's own position: a puzzle asks for a move, so
 * the side to move in it is the person being asked.
 */
export function whitePov(puzzleFen: string, score: Score | null | undefined): Score | null {
  if (!score || typeof score.value !== "number") return null;
  const solverIsWhite = String(puzzleFen || "").split(" ")[1] !== "b";
  return solverIsWhite ? score : { kind: score.kind, value: -score.value };
}

/** `+1.4`, `-5.0`, `#3`, `#-2` — scores as a chess player writes them. */
export function formatScore(score: Score | null | undefined): string {
  if (!score || typeof score.value !== "number") return "";
  if (score.kind === "mate") return score.value >= 0 ? `#${score.value}` : `#-${Math.abs(score.value)}`;
  const pawns = score.value / 100;
  return (pawns > 0 ? "+" : "") + pawns.toFixed(1);
}

/** How the line ended, when it ended against you; `score` is yours. */
export function verdict(score: Score | null | undefined): string {
  if (!score || typeof score.value !== "number") return "";
  if (score.kind === "mate") return score.value < 0 ? "and it is mate." : "and you have given up what you had.";
  if (score.value <= -500) return "and you are lost.";
  if (score.value <= -200) return "and you are much worse.";
  if (score.value <= -50) return "and you are worse.";
  return "and you have given up what you had.";
}

/** How the line ended, when it ended your way; `score` is yours. */
export function outcome(score: Score | null | undefined): string {
  if (!score || typeof score.value !== "number") return "";
  if (score.kind === "mate") return "and it is mate.";
  if (score.value >= 500) return "and that is winning.";
  if (score.value >= 200) return "and you come out clearly better.";
  return "and the position holds.";
}
